import React, { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  useTheme,
  IconButton,
  Badge,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Divider,
  Tooltip,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  KeyboardArrowDown,
  NotificationsNone,
  Menu as MenuIcon,
  Close as CloseIcon,
  Dashboard,
  Assessment,
  AccountTree,
  AdminPanelSettings,
  AutoAwesome,
  CompareArrows,
  Inventory2,
  Tune,
  AttachMoney,
  ViewQuilt as CustomizeIcon,
  RestartAlt as ResetIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  WarningAmber as WarningIcon,
  ErrorOutline as ErrorIcon,
  CheckCircleOutline as CheckCircleIcon,
  DoneAll as DoneAllIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useDepartment } from '../contexts/DepartmentContext.jsx';
import { useBranding } from '../contexts/BrandingContext.jsx';
import UserAvatar from './auth/UserAvatar.jsx';
import { API_BASE_URL } from '../config';
import { getAuthFetchOptions } from '../utils/auth';

const DEFAULT_NAVBAR_PREFS = {
  topBarItems: {
    dashboard: true,
    inventory: true,
    costs: true,
    explorer: true,
    ai: true,
    optimization: true,
    admin: true,
    departmentFilter: true,
    searchBar: true,
  },
};

function getPrefsStorageKey(user) {
  const id = user?.email || user?.username || user?.id || 'anonymous';
  return `cloudcor_navbar_prefs_${id}`;
}

function getReadNotifsStorageKey(user) {
  const id = user?.email || user?.username || user?.id || 'anonymous';
  return `cloudcor_read_notifications_${id}`;
}

function Navbar() {
  const auth = useAuth();
  const { selectedDepartment, setSelectedDepartment, departments, setCommandPaletteOpen } = useDepartment();
  const { branding } = useBranding();
  const currentUser = auth?.currentUser;
  const isAdmin = auth?.isAdmin;
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [optimizationAnchor, setOptimizationAnchor] = useState(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [navbarPrefs, setNavbarPrefs] = useState(DEFAULT_NAVBAR_PREFS);
  const [savingPrefs, setSavingPrefs] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [readNotifIds, setReadNotifIds] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const canViewFinancials = auth?.canViewFinancials?.() || false;
  const canViewInventory = auth?.canViewInventory?.() || false;

  const checkIsAdmin = () => {
    if (!isAdmin || typeof isAdmin !== 'function') return false;
    try {
      return isAdmin();
    } catch (e) {
      return false;
    }
  };

  const showAdminNavigation = checkIsAdmin();

  // Load per-user navbar preferences from localStorage and server user profile
  useEffect(() => {
    if (!currentUser) {
      setNavbarPrefs(DEFAULT_NAVBAR_PREFS);
      return;
    }
    const key = getPrefsStorageKey(currentUser);
    let loaded = null;
    try {
      const localRaw = localStorage.getItem(key);
      if (localRaw) {
        loaded = JSON.parse(localRaw);
      }
    } catch (_) {}

    const serverConfig = currentUser?.preferences?.navbarConfig;
    const mergedTopBar = {
      ...DEFAULT_NAVBAR_PREFS.topBarItems,
      ...(serverConfig?.topBarItems || {}),
      ...(loaded?.topBarItems || {}),
    };
    setNavbarPrefs({ topBarItems: mergedTopBar });

    // Restore read notification IDs
    try {
      const storedRead = localStorage.getItem(getReadNotifsStorageKey(currentUser));
      if (storedRead) {
        setReadNotifIds(JSON.parse(storedRead));
      } else {
        setReadNotifIds([]);
      }
    } catch (_) {
      setReadNotifIds([]);
    }
  }, [currentUser]);

  const userKey = currentUser?.id || currentUser?.email || currentUser?.username || '';

  // Fetch real notifications (active cost anomalies & failed syncs)
  const fetchNotifications = useCallback(async () => {
    if (!userKey) {
      setNotifications([]);
      return;
    }
    setLoadingNotifications(true);
    const items = [];
    try {
      if (canViewFinancials) {
        const res = await fetch(`${API_BASE_URL}/costs/anomalies?status=open`, getAuthFetchOptions());
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data?.anomalies || [];
          list.slice(0, 8).forEach((a, idx) => {
            const id = `anomaly-${a.id || a.service || idx}`;
            const delta = Number(a.deltaAmount ?? a.delta ?? a.costImpact ?? 0);
            items.push({
              id,
              type: 'warning',
              title: `Cost Anomaly: ${a.service || a.accountName || 'Cloud Service'}`,
              description:
                delta > 0
                  ? `Unusual spend spike of +$${delta.toLocaleString('en-US', { maximumFractionDigits: 0 })} detected.`
                  : a.description || a.reason || 'Unusual cost pattern detected.',
              timestamp: a.detectedAt || a.date || a.createdAt || null,
              path: '/anomalies',
            });
          });
        }
      }
    } catch (_) {}

    try {
      if (showAdminNavigation) {
        const syncRes = await fetch(`${API_BASE_URL}/admin/costs/sync-logs?limit=10`, getAuthFetchOptions());
        if (syncRes.ok) {
          const syncData = await syncRes.json();
          const logs = Array.isArray(syncData) ? syncData : syncData?.logs || [];
          logs
            .filter((l) => l.status === 'failed' || l.status === 'error')
            .slice(0, 5)
            .forEach((l, idx) => {
              const id = `sync-${l.id || l.accountId || idx}`;
              items.push({
                id,
                type: 'error',
                title: `Sync Failed: ${l.accountName || l.provider || 'Cloud Account'}`,
                description: l.errorMessage || l.error || 'Cost sync encountered an error.',
                timestamp: l.completedAt || l.startedAt || l.createdAt || null,
                path: '/admin/cloud-accounts',
              });
            });
        }
      }
    } catch (_) {}

    setNotifications(items);
    setLoadingNotifications(false);
  }, [userKey, canViewFinancials, showAdminNavigation]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markNotificationRead = (id) => {
    if (!currentUser) return;
    setReadNotifIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      try {
        localStorage.setItem(getReadNotifsStorageKey(currentUser), JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  };

  const markAllNotificationsRead = () => {
    if (!currentUser) return;
    const allIds = notifications.map((n) => n.id);
    setReadNotifIds((prev) => {
      const next = Array.from(new Set([...prev, ...allIds]));
      try {
        localStorage.setItem(getReadNotifsStorageKey(currentUser), JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  };

  const unreadNotifications = notifications.filter((n) => !readNotifIds.includes(n.id));
  const unreadCount = unreadNotifications.length;

  const persistNavbarPrefs = useCallback(
    async (nextPrefs) => {
      setNavbarPrefs(nextPrefs);
      if (!currentUser) return;
      const key = getPrefsStorageKey(currentUser);
      try {
        localStorage.setItem(key, JSON.stringify(nextPrefs));
      } catch (_) {}

      try {
        setSavingPrefs(true);
        await fetch(`${API_BASE_URL}/auth/user`, {
          ...getAuthFetchOptions(),
          method: 'PUT',
          body: JSON.stringify({
            preferences: {
              ...(currentUser.preferences || {}),
              navbarConfig: nextPrefs,
            },
          }),
        });
      } catch (_) {
        // LocalStorage already holds the user's preference
      } finally {
        setSavingPrefs(false);
      }
    },
    [currentUser]
  );

  const handleToggleNavPref = (itemKey) => {
    const next = {
      ...navbarPrefs,
      topBarItems: {
        ...navbarPrefs.topBarItems,
        [itemKey]: navbarPrefs.topBarItems[itemKey] === false ? true : false,
      },
    };
    persistNavbarPrefs(next);
  };

  const handleApplyPreset = (preset) => {
    if (preset === 'all') {
      persistNavbarPrefs(DEFAULT_NAVBAR_PREFS);
    } else if (preset === 'compact') {
      persistNavbarPrefs({
        topBarItems: {
          dashboard: true,
          inventory: true,
          costs: true,
          explorer: false,
          ai: false,
          optimization: false,
          admin: true,
          departmentFilter: true,
          searchBar: true,
        },
      });
    } else if (preset === 'minimal') {
      persistNavbarPrefs({
        topBarItems: {
          dashboard: true,
          inventory: false,
          costs: true,
          explorer: false,
          ai: false,
          optimization: false,
          admin: false,
          departmentFilter: false,
          searchBar: true,
        },
      });
    }
  };

  const primaryNavigationItems = [
    { key: 'dashboard', text: 'Dashboard', path: '/', icon: <Dashboard fontSize="small" /> },
    ...(canViewInventory ? [{ key: 'inventory', text: 'Inventory', path: '/inventory', icon: <Inventory2 fontSize="small" /> }] : []),
    ...(canViewFinancials
      ? [
          { key: 'costs', text: 'Costs', path: '/costs', icon: <AttachMoney fontSize="small" />, matchPrefix: '/costs' },
          { key: 'explorer', text: 'Cloud Explorer', path: '/explorer', icon: <AccountTree fontSize="small" /> },
          { key: 'ai', text: 'AI Insights', path: '/ai', icon: <AutoAwesome fontSize="small" /> },
        ]
      : []),
  ];

  const optimizationItems = canViewFinancials
    ? [
        { text: 'Right-Sizing', path: '/rightsizing', icon: <Tune fontSize="small" /> },
        { text: 'Forecast', path: '/forecast', icon: <Assessment fontSize="small" /> },
        { text: 'Cost Comparison', path: '/comparison', icon: <CompareArrows fontSize="small" /> },
      ]
    : [];

  const adminNavigationItem = { key: 'admin', text: 'Admin', path: '/admin', icon: <AdminPanelSettings fontSize="small" /> };

  const visiblePrimaryItems = primaryNavigationItems.filter((item) => navbarPrefs.topBarItems[item.key] !== false);
  const showOptimizationInTopBar = optimizationItems.length > 0 && navbarPrefs.topBarItems.optimization !== false;
  const showAdminInTopBar = showAdminNavigation && navbarPrefs.topBarItems.admin !== false;
  const showDepartmentInTopBar = navbarPrefs.topBarItems.departmentFilter !== false;
  const showSearchInTopBar = navbarPrefs.topBarItems.searchBar !== false;

  const allDrawerItems = [
    ...primaryNavigationItems,
    ...optimizationItems.map((item) => ({ ...item, key: 'optimization' })),
    ...(showAdminNavigation ? [adminNavigationItem] : []),
  ];

  const hiddenDrawerItems = allDrawerItems.filter((item) => navbarPrefs.topBarItems[item.key] === false);
  const pinnedDrawerItems = allDrawerItems.filter((item) => navbarPrefs.topBarItems[item.key] !== false);

  const optimizationActive = optimizationItems.some((item) => location.pathname === item.path);

  const toggleDrawer = (open) => (event) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) return;
    setDrawerOpen(open);
  };

  const hasCoBranding = Boolean(
    branding?.showCompanyLogo && (branding?.logoData || (branding?.companyName && branding.companyName.trim()))
  );

  const BrandLockup = (
    <Box
      component={Link}
      to="/"
      sx={{
        textDecoration: 'none',
        color: theme.palette.text.primary,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        flexShrink: 0,
      }}
    >
      {/* CloudCOR Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center', width: 32, height: 32, flexShrink: 0 }}>
        <svg viewBox="0 0 32 32" aria-hidden="true" width="100%" height="100%">
          <path d="M25 9a12 12 0 1 0 0 14" fill="none" stroke="#1E90C3" strokeWidth="3.4" strokeLinecap="round" />
          <path d="M20 13a6.5 6.5 0 1 0 0 6" fill="none" stroke="#6FBBD7" strokeWidth="3.2" strokeLinecap="round" />
        </svg>
      </Box>
      <Box
        component="span"
        sx={{
          display: { xs: 'none', sm: 'inline' },
          fontWeight: 800,
          fontSize: '1.35rem',
          letterSpacing: '-0.02em',
          whiteSpace: 'nowrap',
        }}
      >
        CloudCOR
      </Box>

      {/* Co-Branded Partner Edition */}
      {hasCoBranding && (
        <>
          <Box
            sx={{
              height: 22,
              width: '1.5px',
              bgcolor: 'rgba(148, 163, 184, 0.3)',
              mx: 0.25,
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {branding.logoData ? (
              <>
                <Box
                  component="img"
                  src={branding.logoData}
                  alt={branding.companyName || 'Company Logo'}
                  sx={{
                    maxHeight: 14,
                    maxWidth: { xs: 72, sm: 92 },
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.62rem',
                    color: '#94A3B8',
                    letterSpacing: '0.03em',
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                    mt: '2px',
                    textAlign: 'right',
                  }}
                >
                  Edition
                </Typography>
              </>
            ) : (
              branding.companyName && (
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    color: '#94A3B8',
                    letterSpacing: '0.01em',
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                  }}
                >
                  {/\bedition$/i.test(branding.companyName.trim())
                    ? branding.companyName.trim()
                    : `${branding.companyName.trim()} Edition`}
                </Typography>
              )
            )}
          </Box>
        </>
      )}
    </Box>
  );

  const DrawerList = (
    <Box
      sx={{
        width: { xs: 300, sm: 330 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.paper,
      }}
      role="presentation"
    >
      {/* Drawer Header */}
      <Box
        sx={{
          px: 2,
          py: 1.75,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box onClick={toggleDrawer(false)} sx={{ minWidth: 0, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
          {BrandLockup}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {currentUser && (
            <Tooltip title="Customize Top Bar">
              <IconButton
                size="small"
                onClick={() => {
                  setDrawerOpen(false);
                  setCustomizeOpen(true);
                }}
                color="primary"
              >
                <CustomizeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close Menu">
            <IconButton size="small" onClick={toggleDrawer(false)} sx={{ color: 'text.secondary' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {currentUser && (
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1, textTransform: 'uppercase' }}>
            Department Filter
          </Typography>
          <FormControl size="small" fullWidth>
            <Select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              displayEmpty
              sx={{
                borderRadius: 2,
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={() => {
              setDrawerOpen(false);
              setCommandPaletteOpen(true);
            }}
            sx={{ mt: 1.5, borderRadius: 2, textTransform: 'none', justifyContent: 'flex-start', color: 'text.secondary' }}
          >
            Quick Search (Cmd+K)
          </Button>
        </Box>
      )}

      {currentUser && (
        <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
          {hiddenDrawerItems.length > 0 && (
            <>
              <Box sx={{ px: 2.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  variant="caption"
                  color="primary"
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  Tucked in Hamburger Menu ({hiddenDrawerItems.length})
                </Typography>
              </Box>
              <List dense disablePadding>
                {hiddenDrawerItems.map((item) => {
                  const isActive = item.matchPrefix ? location.pathname.startsWith(item.matchPrefix) : location.pathname === item.path;
                  return (
                    <ListItem
                      key={item.path}
                      disablePadding
                      secondaryAction={
                        <Tooltip title="Restore to Top Bar">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleNavPref(item.key);
                            }}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                          >
                            <VisibilityOffIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemButton
                        component={Link}
                        to={item.path}
                        selected={isActive}
                        onClick={toggleDrawer(false)}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isActive ? 800 : 600 }} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          <Box sx={{ px: 2.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
            >
              Pinned in Top Bar
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
              Click eye to hide from top bar
            </Typography>
          </Box>
          <List dense disablePadding>
            {pinnedDrawerItems.map((item) => {
              const isActive = item.matchPrefix ? location.pathname.startsWith(item.matchPrefix) : location.pathname === item.path;
              return (
                <ListItem
                  key={item.path}
                  disablePadding
                  secondaryAction={
                    <Tooltip title="Hide from Top Bar (Keep in Hamburger Menu)">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleNavPref(item.key);
                        }}
                        sx={{ color: 'primary.main', opacity: 0.75, '&:hover': { opacity: 1 } }}
                      >
                        <VisibilityIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    selected={isActive}
                    onClick={toggleDrawer(false)}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isActive ? 800 : 600 }} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      {currentUser && (
        <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<CustomizeIcon />}
            onClick={() => {
              setDrawerOpen(false);
              setCustomizeOpen(true);
            }}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Customize Navigation Bar
          </Button>
        </Box>
      )}
    </Box>
  );

  const customizableOptions = [
    { key: 'dashboard', label: 'Dashboard', description: 'Main executive cloud spend overview', available: true },
    { key: 'inventory', label: 'Inventory', description: 'Discovered cloud resources & tagging', available: canViewInventory },
    { key: 'costs', label: 'Costs Hub', description: 'Providers, applications, and department chargebacks', available: canViewFinancials },
    { key: 'explorer', label: 'Cloud Explorer', description: 'Interactive multi-cloud cost explorer', available: canViewFinancials },
    { key: 'ai', label: 'AI Insights', description: 'AI anomaly detection & savings recommendations', available: canViewFinancials },
    { key: 'optimization', label: 'Optimization Menu', description: 'Right-Sizing, Forecast, and Cost Comparison', available: canViewFinancials },
    { key: 'admin', label: 'Admin Console', description: 'System configuration, departments, branding & users', available: showAdminNavigation },
    { key: 'departmentFilter', label: 'Department Selector Pill', description: 'Filter view by department in top bar', available: true },
    { key: 'searchBar', label: 'Quick Search Bar (Cmd+K)', description: 'Search bar in the top navigation bar', available: true },
  ].filter((opt) => opt.available);

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar
          sx={{
            px: { xs: 1.5, sm: 2.5, md: 3.5 },
            py: 1,
            gap: { xs: 1, md: 2 },
            justifyContent: 'space-between',
            minHeight: '68px !important',
            flexWrap: 'nowrap',
          }}
        >
          {/* Left Section: Hamburger Menu, Co-Branded Logo & Department Dropdown */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2, md: 2.5 }, minWidth: 0, flexShrink: 0 }}>
            {currentUser && (
              <Tooltip title="Navigation Menu & Customize Bar">
                <IconButton
                  aria-label="Open Navigation Menu"
                  onClick={toggleDrawer(true)}
                  sx={{
                    color: 'text.primary',
                    borderRadius: 2,
                    p: 0.85,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.06)',
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
            )}

            {BrandLockup}

            {/* Department Dropdown Pill */}
            {currentUser && showDepartmentInTopBar && (
              <Box sx={{ display: { xs: 'none', xl: 'flex' }, alignItems: 'center' }}>
                <FormControl size="small">
                  <Select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    displayEmpty
                    sx={{
                      height: 38,
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      borderRadius: '14px',
                      bgcolor: 'rgba(37, 39, 44, 0.4)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#E2E8F0',
                      '& .MuiSelect-select': { py: 0.5, pr: 3.5, pl: 1.75, display: 'flex', alignItems: 'center' },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover': { bgcolor: 'rgba(37, 39, 44, 0.8)' },
                      '& .MuiSvgIcon-root': { color: '#94A3B8' },
                    }}
                  >
                    {departments.map((d) => (
                      <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.62rem', color: '#94A3B8', fontWeight: 600, lineHeight: 1 }}>
                            Department
                          </Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.2, mt: 0.2 }}>
                            {d.name}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

          {/* Middle Section: Customizable Desktop Navigation & Search */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { md: 1, lg: 2 },
              minWidth: 0,
            }}
          >
            {currentUser && (
              <>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    gap: { md: 0.25, lg: 0.5 },
                    flexShrink: 0,
                  }}
                >
                  {visiblePrimaryItems.map((item) => {
                    const isActive = item.matchPrefix
                      ? location.pathname.startsWith(item.matchPrefix)
                      : location.pathname === item.path;
                    return (
                      <Button
                        key={item.text}
                        component={Link}
                        to={item.path}
                        size="small"
                        sx={{
                          textTransform: 'none',
                          color: isActive ? 'primary.main' : 'text.primary',
                          fontWeight: isActive ? 800 : 600,
                          fontSize: { md: '0.8rem', lg: '0.85rem' },
                          px: { md: 1, lg: 1.25 },
                          py: 0.65,
                          borderRadius: 2,
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          bgcolor: isActive ? 'rgba(30, 144, 195, 0.15)' : 'transparent',
                          '&:hover': {
                            bgcolor: theme.palette.action.hover,
                            color: 'primary.main',
                          },
                        }}
                      >
                        {item.text}
                      </Button>
                    );
                  })}

                  {showOptimizationInTopBar && (
                    <Button
                      size="small"
                      onClick={(e) => setOptimizationAnchor(e.currentTarget)}
                      endIcon={<KeyboardArrowDown fontSize="small" />}
                      sx={{
                        textTransform: 'none',
                        color: optimizationActive ? 'primary.main' : 'text.primary',
                        fontWeight: optimizationActive ? 800 : 600,
                        fontSize: { md: '0.8rem', lg: '0.85rem' },
                        px: { md: 1, lg: 1.25 },
                        py: 0.65,
                        borderRadius: 2,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        bgcolor: optimizationActive ? 'rgba(30, 144, 195, 0.15)' : 'transparent',
                      }}
                    >
                      Optimization
                    </Button>
                  )}

                  {showAdminInTopBar && (
                    <Button
                      component={Link}
                      to={adminNavigationItem.path}
                      size="small"
                      sx={{
                        textTransform: 'none',
                        color: location.pathname.startsWith('/admin') ? 'primary.main' : 'text.primary',
                        fontWeight: location.pathname.startsWith('/admin') ? 800 : 600,
                        fontSize: { md: '0.8rem', lg: '0.85rem' },
                        px: { md: 1, lg: 1.25 },
                        py: 0.65,
                        borderRadius: 2,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        bgcolor: location.pathname.startsWith('/admin') ? 'rgba(30, 144, 195, 0.15)' : 'transparent',
                      }}
                    >
                      Admin
                    </Button>
                  )}
                </Box>

                {/* Search Bar */}
                {showSearchInTopBar && (
                  <Box
                    onClick={() => setCommandPaletteOpen(true)}
                    sx={{
                      display: { xs: 'none', lg: 'flex' },
                      alignItems: 'center',
                      bgcolor: 'rgba(37, 39, 44, 0.6)',
                      border: '1px solid rgba(56, 189, 248, 0.4)',
                      borderRadius: '24px',
                      px: 1.75,
                      py: 0.65,
                      cursor: 'text',
                      width: { lg: 165, xl: 220 },
                      minWidth: 120,
                      flexShrink: 1,
                      boxShadow: '0 0 10px rgba(56, 189, 248, 0.1)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'rgba(56, 189, 248, 0.8)',
                        boxShadow: '0 0 15px rgba(56, 189, 248, 0.2)',
                      },
                    }}
                  >
                    <SearchIcon sx={{ color: '#94A3B8', fontSize: 18, mr: 0.75, flexShrink: 0 }} />
                    <Typography
                      sx={{
                        color: '#64748B',
                        fontSize: '0.82rem',
                        flexGrow: 1,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      Search...
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.1)',
                        borderRadius: '4px',
                        px: 0.65,
                        py: 0.15,
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid rgba(255,255,255,0.2)',
                        ml: 0.75,
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ color: '#E2E8F0', fontSize: '0.62rem', fontWeight: 700 }}>Cmd+K</Typography>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Right Section: Customize Nav, Notifications & User */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexShrink: 0 }}>
            {currentUser && (
              <>
                <Tooltip title="Customize Navigation Bar (Show/Hide Items in Hamburger Menu)">
                  <IconButton
                    size="small"
                    aria-label="Customize Navigation Bar"
                    onClick={() => setCustomizeOpen(true)}
                    sx={{ color: '#94A3B8', '&:hover': { color: 'primary.main' } }}
                  >
                    <CustomizeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Notification Bell */}
                <Tooltip title={unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'Notifications'}>
                  <IconButton
                    size="small"
                    aria-label="Notifications"
                    onClick={(e) => setNotificationsAnchor(e.currentTarget)}
                    sx={{
                      color: Boolean(notificationsAnchor) ? 'primary.main' : '#94A3B8',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <Badge
                      badgeContent={unreadCount}
                      color="primary"
                      invisible={unreadCount === 0}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          height: 16,
                          minWidth: 16,
                          fontWeight: 700,
                        },
                      }}
                    >
                      <NotificationsNone sx={{ fontSize: 22 }} />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* User Profile */}
                <UserAvatar />
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hamburger Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{ zIndex: theme.zIndex.appBar + 100 }}
      >
        {DrawerList}
      </Drawer>

      {/* Notifications Popover */}
      <Popover
        open={Boolean(notificationsAnchor)}
        anchorEl={notificationsAnchor}
        onClose={() => setNotificationsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: { xs: 320, sm: 380 },
            maxHeight: 460,
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={800}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} new`}
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {unreadCount > 0 && (
              <Tooltip title="Mark all as read">
                <IconButton size="small" onClick={markAllNotificationsRead} color="primary">
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Refresh notifications">
              <IconButton size="small" onClick={fetchNotifications} disabled={loadingNotifications}>
                {loadingNotifications ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
          {notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1, opacity: 0.85 }} />
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                You&apos;re all caught up
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No active cost anomalies or account sync alerts right now.
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                {canViewFinancials && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setNotificationsAnchor(null);
                      navigate('/anomalies');
                    }}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    View Anomalies
                  </Button>
                )}
                {showAdminNavigation && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setNotificationsAnchor(null);
                      navigate('/admin/cloud-accounts');
                    }}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Cloud Accounts
                  </Button>
                )}
              </Stack>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((notif, idx) => {
                const isUnread = !readNotifIds.includes(notif.id);
                return (
                  <React.Fragment key={notif.id}>
                    {idx > 0 && <Divider component="li" />}
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => {
                          markNotificationRead(notif.id);
                          setNotificationsAnchor(null);
                          if (notif.path) navigate(notif.path);
                        }}
                        sx={{
                          py: 1.5,
                          px: 2,
                          alignItems: 'flex-start',
                          bgcolor: isUnread ? 'rgba(30, 144, 195, 0.08)' : 'transparent',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                          {notif.type === 'error' ? (
                            <ErrorIcon color="error" fontSize="small" />
                          ) : (
                            <WarningIcon color="warning" fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Typography variant="body2" fontWeight={isUnread ? 700 : 600}>
                                {notif.title}
                              </Typography>
                              {isUnread && (
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.main',
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {notif.description}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>

      {/* Optimization Menu */}
      <Menu
        anchorEl={optimizationAnchor}
        open={Boolean(optimizationAnchor)}
        onClose={() => setOptimizationAnchor(null)}
      >
        {optimizationItems.map((item) => (
          <MenuItem
            key={item.text}
            component={Link}
            to={item.path}
            onClick={() => setOptimizationAnchor(null)}
            selected={location.pathname === item.path}
            sx={{ gap: 1.5, fontSize: '0.875rem', fontWeight: 600 }}
          >
            {item.icon}
            {item.text}
          </MenuItem>
        ))}
      </Menu>

      {/* Per-User Navbar Customization Dialog */}
      <Dialog
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CustomizeIcon color="primary" />
            <span>Customize Your Navigation Bar</span>
          </Box>
          {savingPrefs && <Chip label="Saving..." size="small" color="primary" variant="outlined" />}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which items stay pinned in your top navigation bar. Any item you turn off here remains easily accessible inside the{' '}
            <strong>Hamburger Menu</strong> (top-left) to keep your workspace clean and responsive.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
            <Button size="small" variant="outlined" onClick={() => handleApplyPreset('all')} startIcon={<ResetIcon />} sx={{ textTransform: 'none', borderRadius: 2 }}>
              Show All in Top Bar
            </Button>
            <Button size="small" variant="outlined" onClick={() => handleApplyPreset('compact')} sx={{ textTransform: 'none', borderRadius: 2 }}>
              Compact Preset
            </Button>
            <Button size="small" variant="outlined" onClick={() => handleApplyPreset('minimal')} sx={{ textTransform: 'none', borderRadius: 2 }}>
              Minimal Preset
            </Button>
          </Stack>

          <Stack spacing={1.25}>
            {customizableOptions.map((opt) => {
              const isPinned = navbarPrefs.topBarItems[opt.key] !== false;
              return (
                <Box
                  key={opt.key}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {opt.label}
                      </Typography>
                      <Chip
                        label={isPinned ? 'Top Bar' : 'In Hamburger Menu'}
                        size="small"
                        color={isPinned ? 'primary' : 'default'}
                        variant={isPinned ? 'filled' : 'outlined'}
                        sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {opt.description}
                    </Typography>
                  </Box>
                  <Switch
                    checked={isPinned}
                    onChange={() => handleToggleNavPref(opt.key)}
                    color="primary"
                  />
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="contained" onClick={() => setCustomizeOpen(false)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Navbar;
